import { Moment } from "moment-timezone";
import { useEffect, useMemo, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { AnyPeriod, AnyPrice, castPeriod, castPrice } from "@ct-react/core";
import { useLocaleFormatter } from "@ct-react/locale";
import { calculateCartPaymentAmount } from "@ct-react/calendar";
import { CartItemPrice } from "@shared/graphql";
import { pricing } from "../../i18n/sharable-defs";
import "./payment-choice.scss";

const transDefs = defineMessages({
  down: { id: "cart.detail.down-label", defaultMessage: "{rate} à payer aujourd'hui" },
  fullTitle: { id: "cart.payment-choice.full-title", defaultMessage: "Payer le montant total" },
  partialTitle: { id: "cart.payment-choice.partial-title", defaultMessage: "Payer une partie maintenant et l'autre plus tard" },
  partialDetail: { id: "cart.payment-choice.partial-detail", defaultMessage: "Payez {down} maintenant et le solde ({diff}) vous sera prélevé le {date}." },
  partialCost: { id: "cart.payment-choice.partial-cost", defaultMessage: "Aucun frais supplémentaires." }
});

type Props = {
  item: { period: AnyPeriod, price: CartItemPrice, downPayment: { rate: number, providerCharge?: AnyPrice } };
  onChoice: (hasFullPayment: boolean) => void;
}

const CheckoutPaymentChoice = ({ item, onChoice }: Props) => {

  const intl = useIntl();
  const { print } = useLocaleFormatter();

  const [ selection, setSelection ] = useState<"full" | "partial">(item.downPayment.rate === 1 ? "full" : "partial");

  const full = useMemo(() => calculateCartPaymentAmount([ item ], "full"), [ item ]);
  const partial = useMemo(() => calculateCartPaymentAmount([ item ], "partial"), [ item ]);
  const resume = useMemo(() => selection === "full" ? full : partial, [ selection ]);
  const nextPaymentDate = useMemo<Moment>(() => castPeriod(item.period).start.add(-30, "day"), [ item.period ]);

  const partialDiff = useMemo<AnyPrice>(() => {
    const castedFull = castPrice(full.total);
    const castedPartial = castPrice(partial.total);
    return {
      amount: castedFull.amount - castedPartial.amount,
      currency: castedPartial.currency
    };
  }, [ full, partial ]);

  useEffect(() => onChoice(selection === "full"), [ selection]);

  return (
    <>
      <dl>
        <dt>{intl.formatMessage(pricing.total)}</dt>
        <dd>{print.price(item.price.amount)}</dd>
        {!!resume.detailed.providerCharge &&
          <>
            <dt>{intl.formatMessage(pricing.chargeLabel)}</dt>
            <dd>{print.price(resume.detailed.providerCharge)}</dd>
          </>
        }
      </dl>
      {item.downPayment.rate < 1 &&
        <div className="payment-choice">
          <label htmlFor="partial" className="grouped-radio">
            <input type="radio" id="partial" value="partial"
                   checked={selection === "partial"}
                   onChange={() => setSelection("partial")} />
            <span className="label">{intl.formatMessage(transDefs.partialTitle)}</span>
            <div className="detail">
              <span className="block">
                {intl.formatMessage(transDefs.partialDetail, {
                  down: print.price(partial.total),
                  diff: print.price(partialDiff),
                  date: intl.formatDate(nextPaymentDate.toDate(), { day: "2-digit", month: "short", year: "numeric" })
                })}
              </span>
              <span className="block">{intl.formatMessage(transDefs.partialCost)}</span>
            </div>
          </label>
          <label htmlFor="full" className="grouped-radio">
            <input type="radio" id="full" value="full"
                   checked={selection === "full"}
                   onChange={() => setSelection("full")} />
            <span className="label">{intl.formatMessage(transDefs.fullTitle)}</span>
          </label>
        </div>
      }
      <dl>
        <dt className="tot">
          {intl.formatMessage(transDefs.down, {
            rate: intl.formatNumber(selection === "full" ? 1 : item.downPayment.rate, { style: "percent" })
          })}
        </dt>
        <dd className="tot">{print.price(resume.total)}</dd>
      </dl>
    </>);
};

export default CheckoutPaymentChoice;
